import React, { useEffect, useRef, useState } from "react";
import * as xlsx from "xlsx";
import { Modal } from "react-bootstrap";

export default function BySheet2() {
  /*   All States
   ********************************************* */
  // Refs
  const fileInputRef = useRef(null);

  // Excel handling states
  const [loading, setLoading] = useState(false);
  const [wb, setWb] = useState(null);
  const [ws, setWs] = useState(null);
  const [showWorkSheetModal, setShowWorkSheetModal] = useState(false);
  const [spreadsheetData, setSpreadsheetData] = useState([]);
  const [columnLabels, setColumnLabels] = useState([]);
  const [jsonData, setJsonData] = useState([]);

  // User selection states
  const [sortColumns, setSortColumns] = useState([]);
  const [sortDirections, setSortDirections] = useState({});
  const [sumColumns, setSumColumns] = useState([]);
  const [groupByColumn, setGroupByColumn] = useState("");
  const [processedData, setProcessedData] = useState([]);
  const [errors, setErrors] = useState([]);

  /*   All Functions
   ********************************************* */
  const loadingOn = () => setLoading(true);
  const loadingOff = () => setLoading(false);

  // Excel import function
  const readExcel = (e) => {
    let file = e.target.files[0];
    loadingOn();
    const promise = new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      if (!file) {
        loadingOff();
        return;
      }
      fileReader.readAsArrayBuffer(file);

      fileReader.onload = (e) => {
        const bufferArray = e.target.result;
        const wb = xlsx.read(bufferArray, { type: "buffer" });
        resolve(wb);
        loadingOff();
      };

      fileReader.onerror = (error) => {
        reject(error);
        loadingOff();
      };
    });

    promise.then((d) => {
      setWb(d);
    });
  };

  // Process data from selected sheet
  function getDataFromSelectedSheet(sheetName) {
    resetEverything();
    let selectedSheet = wb.Sheets[sheetName];
    const sheetData = xlsx.utils.sheet_to_json(selectedSheet, { header: 1 });

    const [firstRow, ...restRows] = sheetData;
    setColumnLabels(firstRow || []);

    // Convert to display format
    const formattedData = restRows.map((row) =>
      row.map((cell) => ({ value: cell, readOnly: true }))
    );
    setSpreadsheetData(formattedData);

    // Convert to JSON format for processing
    const jsonDataFromSheet = restRows.map((row) => {
      const obj = {};
      firstRow.forEach((header, idx) => {
        obj[header] = row[idx];
      });
      return obj;
    });
    setJsonData(jsonDataFromSheet);

    setShowWorkSheetModal(false);
  }

  // Reset states
  const resetEverything = () => {
    setSpreadsheetData([]);
    setColumnLabels([]);
    setJsonData([]);
    setSortColumns([]);
    setSortDirections({});
    setSumColumns([]);
    setGroupByColumn("");
    setProcessedData([]);
    setErrors([]);
  };

  // Clear all data and reset file input
  const handleClearButton = () => {
    resetEverything();
    setWb(null);
    setWs(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Toggle sort column selection
  const toggleSortColumn = (column) => {
    if (sortColumns.includes(column)) {
      setSortColumns(sortColumns.filter((col) => col !== column));
      // Toggle direction if already in sort columns
      const newDirections = { ...sortDirections };
      if (newDirections[column]) {
        delete newDirections[column];
      }
      setSortDirections(newDirections);
    } else {
      setSortColumns([...sortColumns, column]);
      // Set default direction to ascending
      setSortDirections({ ...sortDirections, [column]: "asc" });
    }
  };

  // Toggle sort direction
  const toggleSortDirection = (column) => {
    if (sortDirections[column] === "asc") {
      setSortDirections({ ...sortDirections, [column]: "desc" });
    } else {
      setSortDirections({ ...sortDirections, [column]: "asc" });
    }
  };

  // Toggle sum column selection
  const toggleSumColumn = (column) => {
    if (sumColumns.includes(column)) {
      setSumColumns(sumColumns.filter((col) => col !== column));
    } else {
      setSumColumns([...sumColumns, column]);
    }
  };

  // Process data based on user selections
  const processData = () => {
    setErrors([]);
    const newErrors = [];

    if (!groupByColumn) {
      newErrors.push("Please select a column to group by");
    }

    if (sumColumns.length === 0) {
      newErrors.push("Please select at least one column for summing values");
    }

    // Validate numeric values in sum columns
    if (sumColumns.length > 0) {
      const nonNumericValues = [];

      jsonData.forEach((row, idx) => {
        sumColumns.forEach((col) => {
          const value = row[col];
          if (value !== undefined && value !== null && isNaN(Number(value))) {
            nonNumericValues.push(
              `Row ${idx + 1}, Column "${col}": "${value}" is not a number`
            );
          }
        });
      });

      if (nonNumericValues.length > 0) {
        // Limit error messages to avoid flooding
        newErrors.push(
          `Found non-numeric values in sum columns: ${nonNumericValues
            .slice(0, 5)
            .join(", ")}${
            nonNumericValues.length > 5
              ? ` and ${nonNumericValues.length - 5} more...`
              : ""
          }`
        );
      }
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }

    // Sort data
    let sortedData = [...jsonData];
    if (sortColumns.length > 0) {
      sortedData.sort((a, b) => {
        for (const col of sortColumns) {
          const direction = sortDirections[col] === "desc" ? -1 : 1;

          if (a[col] < b[col]) return -1 * direction;
          if (a[col] > b[col]) return 1 * direction;
        }
        return 0;
      });
    }

    // Group and combine data
    const groupedData = {};
    sortedData.forEach((row) => {
      const key = row[groupByColumn];
      if (!groupedData[key]) {
        // Create new group
        groupedData[key] = { ...row };
      } else {
        // Add values for selected sum columns
        sumColumns.forEach((col) => {
          const currentValue = Number(groupedData[key][col]) || 0;
          const newValue = Number(row[col]) || 0;
          groupedData[key][col] = currentValue + newValue;
        });
      }
    });

    // Convert back to array
    const result = Object.values(groupedData);
    setProcessedData(result);
  };

  // Export data to Excel
  const exportToExcel = () => {
    if (processedData.length === 0) return;

    // Create a new workbook
    const wb = xlsx.utils.book_new();

    // Convert processed data to worksheet format
    const wsData = [
      [...sortColumns, ...sumColumns],
      ...processedData.map((row) =>
        [...sortColumns, ...sumColumns].map((col) => row[col])
      ),
    ];

    const ws = xlsx.utils.aoa_to_sheet(wsData);

    // Add the worksheet to the workbook
    xlsx.utils.book_append_sheet(wb, ws, "Processed Data");

    // Generate Excel file and trigger download
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(
      today.getMonth() + 1
    ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const timeStr = `${String(today.getHours()).padStart(2, "0")}${String(
      today.getMinutes()
    ).padStart(2, "0")}`;
    const fileName = `processed_data_${dateStr}_${timeStr}.xlsx`;

    xlsx.writeFile(wb, fileName);
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    if (wb) {
      resetEverything();
      setWs(wb.SheetNames);
      setShowWorkSheetModal(true);
    }
  }, [wb]);

  return (
    <div className="container-fluid ps-0 overflow-y-auto h-100">
      <h2 className="">Sheet Processing</h2>

      {/* Excel Import Section */}
      <div className="card">
        <div className="px-3 bg-primary text-white">
          <h5 className="">Import Excel Sheet</h5>
        </div>
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex gap-1 align-items-center">
              <input
                onChange={readExcel}
                ref={fileInputRef}
                type="file"
                className="form-control"
              />

              {ws && (
                <button
                  onClick={() => setShowWorkSheetModal(true)}
                  className="btn text-nowrap btn-info"
                >
                  Change Sheet
                </button>
              )}

              <button onClick={handleClearButton} className="btn btn-danger">
                Clear
              </button>
            </div>

            {columnLabels.length > 0 && (
              <div>
                <span className="badge fs-5 bg-success">
                  {columnLabels.length} columns and {jsonData.length} rows
                  loaded
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Configuration Section */}
      {columnLabels.length > 0 && (
        <div className="row ">
          {/* Sort Columns Selection */}
          <div className="col-md-4">
            <div className="card ">
              <div className=" px-3 bg-secondary text-white">
                <h5 className="">Sort Columns</h5>
              </div>
              <div
                className="card-body"
                style={{ maxHeight: "200px", overflowY: "auto" }}
              >
                <p className="text-muted">
                  Select columns to sort by (in order of importance)
                </p>

                {columnLabels.map((column, index) => (
                  <div
                    key={index}
                    className="form-check d-flex align-items-center mb-2"
                  >
                    <input
                      className="form-check-input me-2"
                      type="checkbox"
                      id={`sort-${column}`}
                      checked={sortColumns.includes(column)}
                      onChange={() => toggleSortColumn(column)}
                    />
                    <label
                      className="form-check-label me-2"
                      htmlFor={`sort-${column}`}
                    >
                      {column}
                    </label>

                    {sortColumns.includes(column) && (
                      <button
                        className={`btn btn-sm ${
                          sortDirections[column] === "asc"
                            ? "btn-outline-success"
                            : "btn-outline-danger"
                        }`}
                        onClick={() => toggleSortDirection(column)}
                      >
                        {sortDirections[column] === "asc" ? "↑ ASC" : "↓ DESC"}
                      </button>
                    )}

                    {sortColumns.includes(column) && (
                      <span className="badge bg-info ms-2">
                        {sortColumns.indexOf(column) + 1}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sum Columns Selection */}
          <div className="col-md-4">
            <div className="card mb-4">
              <div className=" px-3 bg-warning">
                <h5 className="">Sum Columns</h5>
              </div>
              <div
                className="card-body"
                style={{ maxHeight: "200px", overflowY: "auto" }}
              >
                <p className="text-muted">
                  Select numeric columns to sum when combining rows
                </p>

                {columnLabels.map((column, index) => (
                  <div key={index} className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`sum-${column}`}
                      checked={sumColumns.includes(column)}
                      onChange={() => toggleSumColumn(column)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`sum-${column}`}
                    >
                      {column}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Group By Column */}
          <div className="col-md-4">
            <div className="card ">
              <div className=" px-3 bg-success text-white">
                <h5 className="">Group By Column</h5>
              </div>
              <div className="card-body mb-0">
                <p className="text-muted">Select column to group values by</p>
                <select
                  className="form-select"
                  value={groupByColumn}
                  onChange={(e) => setGroupByColumn(e.target.value)}
                >
                  <option value="">-- Select Column --</option>
                  {columnLabels.map((column, index) => (
                    <option key={index} value={column}>
                      {column}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              className="btn btn-primary btn-lg w-100 mb-4"
              onClick={processData}
              disabled={!groupByColumn || sumColumns.length === 0}
            >
              Process Data
            </button>

            {errors.length > 0 && (
              <div className="alert alert-danger">
                <ul className="mb-0">
                  {errors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Results Section */}
      {processedData.length > 0 && (
        <div className="card">
          <div className=" px-3 bg-dark text-white d-flex align-items-center justify-content-between">
            <h5 className="">Processed Data ({processedData.length} rows)</h5>
            <button
              className="btn btn-success btn-sm my-2"
              onClick={exportToExcel}
            >
              Export to excel
            </button>
          </div>
          <div className="card-body">
            <div
              className="table-responsive"
              style={{ maxHeight: "280px", overflowY: "auto" }}
            >
              <table className="table table-striped table-bordered">
                <thead>
                  <tr>
                    {[...sortColumns, ...sumColumns].map((column, i) => (
                      <th
                        key={i}
                        className={`
                        ${
                          groupByColumn === column
                            ? "bg-success bg-opacity-25"
                            : ""
                        }
                        ${
                          sumColumns.includes(column)
                            ? "bg-warning bg-opacity-25"
                            : ""
                        }
                      `}
                      >
                        {column}
                        {groupByColumn === column && " (Grouped)"}
                        {sumColumns.includes(column) && " (Summed)"}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {processedData.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {[...sortColumns, ...sumColumns].map((column, colIdx) => (
                        <td key={colIdx}>
                          {sumColumns.includes(column)
                            ? Number(row[column]).toLocaleString()
                            : row[column]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Worksheet Selection Modal */}
      <Modal
        show={showWorkSheetModal}
        onHide={() => setShowWorkSheetModal(false)}
      >
        <Modal.Body className="bg-light">
          <h3>Select WorkSheet</h3>
          <div className="my-3">
            {ws &&
              ws.map((item) => (
                <div
                  key={item}
                  className="bg-white shadow-sm rounded p-2 mb-2 d-flex align-items-center justify-content-between"
                >
                  <div>{item}</div>
                  <div>
                    <button
                      onClick={() => getDataFromSelectedSheet(item)}
                      className="btn btn-sm btn-success"
                    >
                      Select
                    </button>
                  </div>
                </div>
              ))}
          </div>
          <div className="text-end">
            <button
              onClick={() => setShowWorkSheetModal(false)}
              className="btn btn-sm btn-secondary"
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75"
          style={{ zIndex: 1050 }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
}
